import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import type {
  AdminApplicationSummary,
  ApplicationStatus,
} from "@wmart/shared-types";

/** Wire shape of `GET /v1/admin/applications` — snake_case per admin API convention. */
interface ApplicationListResponse {
  items: AdminApplicationSummary[];
  next_cursor: string | null;
}

type StatusFilter = ApplicationStatus | "all";

const STATUS_TONE: Record<ApplicationStatus, Tone> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

const EMPTY_COPY: Record<StatusFilter, string> = {
  pending: "No pending applications",
  approved: "No approved applications",
  rejected: "No rejected applications",
  all: "No applications found",
};

const FILTER_LABELS: Array<{ value: StatusFilter; label: string }> = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export function ApplicationsList() {
  const { notify, notifyProblem } = useToast();
  const [items, setItems] = useState<AdminApplicationSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);

  // Debounce the free-text query — 250 ms, mirrors CustomersList.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  async function fetchPage(reset: boolean, cursor: string | null) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", "20");
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
      if (cursor) params.set("cursor", cursor);
      const data = await api.get<ApplicationListResponse>(
        `/v1/admin/applications?${params.toString()}`,
      );
      setItems((prev) => (reset ? data.items : [...prev, ...data.items]));
      setNextCursor(data.next_cursor);
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load applications");
    } finally {
      setLoading(false);
    }
  }

  // Re-fetch on filter/search change.
  useEffect(() => {
    void fetchPage(true, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedQ]);

  return (
    <div className="flex flex-col gap-6">
      {/* Filter chips + search */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1">
          {FILTER_LABELS.map(({ value, label }) => (
            <Button
              key={value}
              variant={statusFilter === value ? "primary" : "secondary"}
              size="sm"
              onClick={() => setStatusFilter(value)}
            >
              {label}
            </Button>
          ))}
        </div>
        <div className="w-64">
          <Input
            type="search"
            label="Search"
            placeholder="Search by email or business…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      {/* Empty state */}
      {items.length === 0 && !loading ? (
        <EmptyState
          icon="user"
          title={EMPTY_COPY[statusFilter]}
          body="Adjust filters to see more results."
        />
      ) : (
        <>
          <Table>
            <THead>
              <TR>
                <TH>Email</TH>
                <TH>Business</TH>
                <TH>Type</TH>
                <TH>Submitted</TH>
                <TH>Status</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((app) => (
                <TR key={app.id}>
                  <TD>
                    <Link
                      to={`/applications/${app.id}`}
                      className="text-ink-900 hover:underline"
                    >
                      {app.user_email}
                    </Link>
                  </TD>
                  <TD>{app.business_legal_name}</TD>
                  <TD>{app.business_type}</TD>
                  <TD>{formatDate(app.created_at)}</TD>
                  <TD>
                    <StatusPill tone={STATUS_TONE[app.status]}>
                      {app.status}
                    </StatusPill>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>

          {nextCursor ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => void fetchPage(false, nextCursor)}
                disabled={loading}
                loading={loading}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
